import React from "react";
import { cn } from "../../lib/utils";
import { useAppStore } from "../../shared/stores/useAppStore";
import { round } from "../../tools";

// Format value using useAppStore userSettings (locale + round precision)
const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';

  const precision = useAppStore.getState().userSettings.roundPrecision ?? 2;
  const locale = useAppStore.getState().userSettings.numberFormattingLocale || 'en-GB';

  // If it's a string that looks like a number, try to parse it
  if (typeof value === 'string') {
    // Check if it's a percentage string
    if (value.includes('%')) return value;

    const numValue = parseFloat(value.replace(/[^\d.-]/g, ''));
    if (!isNaN(numValue) && /^[\d.,\s-]+$/.test(value.trim())) {
      return round(numValue, precision).toLocaleString(locale, { maximumFractionDigits: precision });
    }
    // Return non-numeric strings as-is
    return value;
  }

  if (typeof value === 'number') {
    return round(value, precision).toLocaleString(locale, { maximumFractionDigits: precision });
  }

  return String(value);
};

export default function TableItem({ table, bold_column_indexes }) {
  if (!table || table.length === 0) {
    return (
      <div className="text-center text-slate-500 py-8">
        No data available
      </div>
    );
  }

  return (
    <div className="mb-4 overflow-auto relative z-0 border border-slate-200 rounded-lg">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-slate-50 sticky top-0 z-20">
          {table.length > 0 && (
            <tr className="border-b border-slate-200">
              {table[0].map((cell, cellIndex) => (
                <th
                  key={cellIndex}
                  className={cn(
                    "p-2 text-xs font-medium text-slate-500 whitespace-nowrap",
                    cellIndex === 0
                      ? "text-left sticky left-0 z-30 bg-slate-50 min-w-[160px]"
                      : "text-center min-w-[100px]"
                  )}
                >
                  {cell}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {table.slice(1).map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="border-b border-slate-200 hover:bg-slate-50"
            >
              {row.map((cell, cellIndex) => {
                const isBold = bold_column_indexes?.includes(cellIndex);
                // Don't format the first column (labels)
                const formattedValue = cellIndex === 0
                  ? (cell !== null && cell !== undefined ? cell : '-')
                  : formatValue(cell);

                return (
                  <td
                    key={cellIndex}
                    className={cn(
                      "p-2 whitespace-nowrap",
                      cellIndex === 0
                        ? "text-left sticky left-0 z-10 bg-white border-r border-slate-100 font-semibold text-slate-700"
                        : "text-center tabular-nums bg-white",
                      isBold && "font-medium"
                    )}
                  >
                    {formattedValue}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
